import { Code, ConnectError } from '@bufbuild/connect';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import api from '@/shared/api/api';
import { DiagnocoinBatch } from '@/shared/api/protocol-ts/api/billing_new/dto_diagnocoin_new_pb';
import { Order } from '@/shared/api/protocol-ts/api/billing_new/dto_order_new_pb';
import { ProductState } from '@/shared/api/protocol-ts/api/billing_new/dto_product_new_pb';
import { AccountInfoStreamDataAccumulatorKeys, PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { debug, isConnectNetworkError } from '@/shared/lib';

import { billingModel } from '@/entities/billing';
import { organizationModel } from '@/entities/organization';

type AccountInfoStreamDataAccumulators = {
  [AccountInfoStreamDataAccumulatorKeys.inventory]: ProductState[];
  [AccountInfoStreamDataAccumulatorKeys.orders]: Order[];
  [AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches]: DiagnocoinBatch[];
};

const dataAccumulators: AccountInfoStreamDataAccumulators = {
  [AccountInfoStreamDataAccumulatorKeys.inventory]: [],
  [AccountInfoStreamDataAccumulatorKeys.orders]: [],
  [AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches]: [],
};

let abortController: AbortController;

export const useAccountInfoStream = () => {
  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const openAccountInfoStream = async () => {
    abortController = new AbortController();

    dispatch(billingModel.actions.setLoading('pending'));

    dataAccumulators[AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches] =
      [];
    dispatch(billingModel.actions.resetDiagnocoins());
    dispatch(billingModel.actions.setDiagnocoinLoading('pending'));

    try {
      const accountInfoStream = api.billing.accountInfoStream(
        { OrganizationID: organizationID },
        { signal: abortController.signal },
      );

      for await (const { Update } of accountInfoStream) {
        switch (Update.case) {
          case 'AccountInfo': {
            dispatch(billingModel.actions.setAccount(Update.value));
            break;
          }

          case 'HistoricalInventory': {
            dataAccumulators[
              AccountInfoStreamDataAccumulatorKeys.inventory
            ].push(Update.value);
            break;
          }

          case 'HistoricalOrder': {
            if (Update.value.Kind.case === 'Billed') {
              dataAccumulators[
                AccountInfoStreamDataAccumulatorKeys.orders
              ].push(Update.value);
            }
            break;
          }

          case 'EndOfHistoricalInventory': {
            dispatch(
              billingModel.actions.setInventory(
                dataAccumulators[
                  AccountInfoStreamDataAccumulatorKeys.inventory
                ],
              ),
            );

            dataAccumulators[AccountInfoStreamDataAccumulatorKeys.inventory] =
              [];

            dispatch(billingModel.actions.setLoading('succeeded'));
            break;
          }

          case 'EndOfHistoricalOrder': {
            dispatch(
              billingModel.actions.setInvoices(
                dataAccumulators[AccountInfoStreamDataAccumulatorKeys.orders],
              ),
            );

            dataAccumulators[AccountInfoStreamDataAccumulatorKeys.orders] = [];
            break;
          }

          case 'Inventory': {
            dispatch(billingModel.actions.updateInventory(Update.value));
            break;
          }

          case 'Order': {
            if (Update.value.Kind.case === 'Billed') {
              dispatch(billingModel.actions.updateOrders(Update.value));
            }
            break;
          }

          case 'DiagnocoinPrices': {
            dispatch(billingModel.actions.setDiagnocoinPrices(Update.value));
            break;
          }

          case 'ReferralRewards': {
            dispatch(billingModel.actions.setReferralRewards(Update.value));
            break;
          }

          case 'DiagnocoinWallet': {
            dispatch(billingModel.actions.setDiagnocoinWallet(Update.value));
            break;
          }

          case 'HistoricalDiagnocoinBatch': {
            dataAccumulators[
              AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches
            ].push(Update.value);
            break;
          }

          case 'EndOfHistoricalDiagnocoinBatch': {
            dispatch(
              billingModel.actions.setDiagnocoinBatches(
                dataAccumulators[
                  AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches
                ],
              ),
            );

            dataAccumulators[
              AccountInfoStreamDataAccumulatorKeys.diagnocoinBatches
            ] = [];

            dispatch(billingModel.actions.setDiagnocoinLoading('succeeded'));
            break;
          }

          case 'DiagnocoinBatch': {
            dispatch(billingModel.actions.updateDiagnocoinBatch(Update.value));
            break;
          }
        }
      }
    } catch (error) {
      const connectError = ConnectError.from(error);

      if (isConnectNetworkError(connectError)) {
        debug('Network error detected, attempting to restart stream...');
        closeAccountInfoStream();
        openAccountInfoStream();
      }

      if (error instanceof ConnectError && error.code !== Code.Canceled) {
        dispatch(billingModel.actions.setLoading('failed'));
        dispatch(billingModel.actions.setDiagnocoinLoading('failed'));

        if (error.code === Code.NotFound) {
          navigate(PATHS.patients);
        }

        console.error(
          'AccountInfoStream: error: ',
          error.message,
          error.code,
          error.rawMessage,
          error,
        );
      }
    }
  };

  const closeAccountInfoStream = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  useEffect(() => {
    if (organizationID) {
      openAccountInfoStream();
    }

    return () => {
      closeAccountInfoStream();
    };
  }, [organizationID]);
};
